import { Head, router } from '@inertiajs/react'
import { useState } from 'react'
import Layout from '../../../components/Layout'

interface Backup {
  id: number
  taken_at: string
  taken_at_display: string
  size_bytes: number
  size_display: string
  record_count: number
  status: string
  checksum: string | null
  error_message: string | null
}

interface Props {
  organization: { id: number; name: string }
  backups: Backup[]
  retention_per_tenant: number
}

export default function AdminBackupsIndex({ organization, backups, retention_per_tenant }: Props) {
  const [creating, setCreating] = useState(false)

  const createBackup = () => {
    if (creating) return
    setCreating(true)
    router.post('/admin/backups', {}, {
      onFinish: () => setCreating(false)
    })
  }

  const deleteBackup = (id: number) => {
    if (!confirm('Delete this backup? This cannot be undone.')) return
    router.delete(`/admin/backups/${id}`)
  }

  return (
    <Layout>
      <Head title="Tenant Backups" />
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-heading font-bold text-text-primary">Tenant Backups</h1>
            <p className="text-sm text-text-muted mt-1">
              Full snapshots of {organization.name}. Automatic backups run Mondays &amp; Thursdays at 02:00 UTC. The most recent {retention_per_tenant} are kept; older ones are removed automatically.
            </p>
          </div>
          <button
            onClick={createBackup}
            disabled={creating}
            className="px-4 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-heading font-semibold hover:bg-[#C7161E] disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create backup now'}
          </button>
        </div>

        {backups.length === 0 ? (
          <div className="bg-white border border-stone/20 rounded-lg p-12 text-center">
            <p className="text-text-muted">No backups yet. Create one now or wait for the next scheduled run.</p>
          </div>
        ) : (
          <div className="bg-white border border-stone/20 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-text-secondary">
                <tr>
                  <th className="text-left py-3 px-4 font-heading font-semibold">Taken at</th>
                  <th className="text-left py-3 px-4 font-heading font-semibold">Records</th>
                  <th className="text-left py-3 px-4 font-heading font-semibold">Size</th>
                  <th className="text-left py-3 px-4 font-heading font-semibold">Status</th>
                  <th className="text-right py-3 px-4 font-heading font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((b) => (
                  <tr key={b.id} className="border-t border-stone/10">
                    <td className="py-3 px-4 text-text-primary">{b.taken_at_display}</td>
                    <td className="py-3 px-4 text-text-secondary">{b.record_count.toLocaleString()}</td>
                    <td className="py-3 px-4 text-text-secondary">{b.size_display}</td>
                    <td className="py-3 px-4">
                      {b.status === 'complete' ? (
                        <span className="px-2 py-0.5 rounded bg-[rgba(21,83,46,0.15)] text-[#15532E] text-xs font-semibold">Complete</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded bg-[rgba(237,28,36,0.15)] text-[#ED1C24] text-xs font-semibold" title={b.error_message || ''}>Failed</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right space-x-2">
                      {b.status === 'complete' && (
                        <a
                          href={`/admin/backups/${b.id}`}
                          className="inline-block px-3 py-1.5 border border-stone/30 rounded text-xs text-text-secondary hover:bg-[#F5F5F5]"
                        >
                          Download
                        </a>
                      )}
                      <button
                        onClick={() => deleteBackup(b.id)}
                        className="px-3 py-1.5 border border-stone/30 rounded text-xs text-[#ED1C24] hover:bg-[rgba(237,28,36,0.1)]"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-8 bg-[#F5F5F5] border border-stone/20 rounded-lg p-4 text-sm text-text-secondary">
          <div className="font-heading font-semibold text-text-primary mb-1">What's in a backup?</div>
          <p>Every organization-scoped table: users, teams, meetings and all their sections, rocks &amp; milestones, scoreboard metrics &amp; entries, action items, topics &amp; votes, SVEP plans, agenda &amp; meeting templates, audit log, invitations, and email settings. Downloaded backups are gzipped JSON.</p>
        </div>
      </div>
    </Layout>
  )
}
